import React, { useState } from 'react';
import { accountingAPI } from '../../../services/api';
import { Table }       from '../../../components/common/Table';
import { Button }      from '../../../components/common/Button';
import { Badge }       from '../../../components/common/Badge';
import { Pagination }  from '../../../components/common/Pagination';
import { Modal }       from '../../../components/common/Modal';
import { AccountingForm }       from './AccountingForm';
import { InvoiceDownloadModal } from './InvoicePDF';
import {
  PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon,
  EyeIcon, ArrowDownTrayIcon, ArrowsRightLeftIcon, BanknotesIcon, PrinterIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

// Which types can be converted to what
const CONVERT_OPTIONS = {
  quotation:        [{ value: 'proforma', label: '→ Proforma Invoice' }, { value: 'sale', label: '→ Tax Invoice' }],
  proforma:         [{ value: 'sale', label: '→ Tax Invoice' }, { value: 'delivery_challan', label: '→ Delivery Challan' }],
  sale:             [{ value: 'delivery_challan', label: '→ Delivery Challan' }],
  purchase:         [{ value: 'debit_note', label: '→ Debit Note' }],
};

const TYPE_LABEL = {
  sale: 'Tax Invoice', purchase: 'Purchase', expense: 'Expense',
  quotation: 'Quotation', indirect_income: 'Income',
  proforma: 'Proforma', delivery_challan: 'Delivery Challan', debit_note: 'Debit Note',
};

export function AccountingList({ type, title, records, loading, pagination, onPageChange, onRefresh, search, setSearch, statusFilter, setStatusFilter }) {
  const [formOpen,     setFormOpen]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [viewing,      setViewing]      = useState(null);
  const [viewOpen,     setViewOpen]     = useState(false);
  const [invoiceRec,   setInvoiceRec]   = useState(null);
  const [invoiceOpen,  setInvoiceOpen]  = useState(false);
  const [convertRec,   setConvertRec]   = useState(null);
  const [convertOpen,  setConvertOpen]  = useState(false);
  const [paymentRec,   setPaymentRec]   = useState(null);
  const [paymentOpen,  setPaymentOpen]  = useState(false);
  const [converting,   setConverting]   = useState(false);

  const openCreate  = ()  => { setEditing(null); setFormOpen(true); };
  const openEdit    = r   => { setEditing(r);    setFormOpen(true); };

  const openView = async r => {
    try { const res = await accountingAPI.getById(r._id); setInvoiceRec(res.data); setInvoiceOpen(true); }
    catch { toast.error('Failed to load record'); }
  };

  const openInvoice = async r => {
    try { const res = await accountingAPI.getById(r._id); setInvoiceRec(res.data); setInvoiceOpen(true); }
    catch { toast.error('Failed to load'); }
  };

  const downloadPdf = async r => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/accounting/${r._id}/pdf`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!res.ok) { toast.error('Failed to generate PDF'); return; }
      const blob    = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a       = document.createElement('a');
      a.href        = blobUrl;
      a.download    = `${r.referenceNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch { toast.error('PDF download failed'); }
  };

  const openConvert = r => { setConvertRec(r); setConvertOpen(true); };
  const openPayment = r => { setPaymentRec(r); setPaymentOpen(true); };

  const handleSave = async formData => {
    try {
      editing
        ? await accountingAPI.update(editing._id, formData)
        : await accountingAPI.create({ ...formData, type });
      toast.success(editing ? 'Updated' : 'Created');
      setFormOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.message || 'Failed'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this record?')) return;
    try { await accountingAPI.delete(id); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleStatus = async (id, status) => {
    try { await accountingAPI.update(id, { status }); toast.success('Status updated'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  const handleConvert = async (targetType) => {
    if (!convertRec) return;
    setConverting(true);
    try {
      await accountingAPI.convert(convertRec._id, targetType);
      toast.success(`Converted to ${TYPE_LABEL[targetType]}`);
      setConvertOpen(false);
      onRefresh();
    } catch (err) { toast.error(err.message || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const handleRecordPayment = async (formData) => {
    if (!paymentRec) return;
    try {
      const newPaid = Number(paymentRec.paidAmount || 0) + Number(formData.amount);
      const status  = newPaid >= Number(paymentRec.total) ? 'paid' : 'pending';
      await accountingAPI.update(paymentRec._id, {
        paidAmount:  newPaid,
        paymentMode: formData.paymentMode,
        paymentDate: formData.paymentDate,
        status,
      });
      toast.success('Payment recorded');
      setPaymentOpen(false);
      onRefresh();
    } catch { toast.error('Failed to record payment'); }
  };

  const convertOptions = CONVERT_OPTIONS[type] || [];

  const STATUSES_BY_TYPE = {
    delivery_challan: ['draft','pending','approved','dispatched','delivered','cancelled'],
    default:          ['draft','pending','approved','paid','cancelled','overdue'],
  };
  const statuses = STATUSES_BY_TYPE[type] || STATUSES_BY_TYPE.default;

  const cols = [
    { header: 'Ref #', render: r => (
      <button onClick={() => openView(r)} className="text-xs font-mono font-semibold text-indigo-600 hover:underline">
        {r.referenceNo}
      </button>
    )},
    { header: 'Date', render: r => (
      <div>
        <p className="text-sm text-gray-800">{format(new Date(r.date), 'dd MMM yyyy')}</p>
        {r.dueDate      && <p className="text-xs text-gray-400">Due {format(new Date(r.dueDate), 'dd MMM')}</p>}
        {r.deliveryDate && <p className="text-xs text-blue-400">Del {format(new Date(r.deliveryDate), 'dd MMM')}</p>}
      </div>
    )},
    { header: 'Party', render: r => (
      <div>
        <p className="text-sm font-medium text-gray-900">{r.contact?.name || r.contactName || '—'}</p>
        {r.contact?.company && <p className="text-xs text-gray-400">{r.contact.company}</p>}
      </div>
    )},
    { header: 'Amount', render: r => (
      <div>
        <p className="font-bold text-gray-900 text-sm">{'₹' + Number(r.total || 0).toLocaleString('en-IN')}</p>
        {r.paidAmount > 0 && r.paidAmount < r.total && <p className="text-xs text-amber-600">Paid ₹{Number(r.paidAmount).toLocaleString('en-IN')}</p>}
        {r.paidAmount >= r.total && r.total > 0      && <p className="text-xs text-green-600">Fully paid</p>}
      </div>
    )},
    { header: 'Status', render: r => (
      <select value={r.status} onChange={e => handleStatus(r._id, e.target.value)}
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium capitalize">
        {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
      </select>
    )},
    { header: 'Payment', render: r => <span className="text-xs text-gray-500 capitalize">{r.paymentMode?.replace('_', ' ') || '—'}</span> },
    { header: '', render: r => (
      <div className="flex gap-1 flex-wrap">
        <button onClick={() => openView(r)}    title="View"         className="p-1.5 hover:bg-blue-50   rounded-lg"><EyeIcon           className="w-4 h-4 text-blue-500"   /></button>
        <button onClick={() => downloadPdf(r)}  title="Download PDF" className="p-1.5 hover:bg-green-50  rounded-lg"><ArrowDownTrayIcon className="w-4 h-4 text-green-600" /></button>
        <button onClick={() => openInvoice(r)}  title="Print"        className="p-1.5 hover:bg-orange-50 rounded-lg"><PrinterIcon       className="w-4 h-4 text-orange-500" /></button>
        {/* Record Payment — only for payable types */}
        {['sale','purchase','proforma'].includes(r.type || type) && r.status !== 'paid' && r.status !== 'cancelled' && (
          <button onClick={() => openPayment(r)} title="Record Payment" className="p-1.5 hover:bg-emerald-50 rounded-lg"><BanknotesIcon className="w-4 h-4 text-emerald-600"/></button>
        )}
        {/* Convert button — only if conversions available */}
        {convertOptions.length > 0 && !r.convertedTo && (
          <button onClick={() => openConvert(r)} title="Convert" className="p-1.5 hover:bg-violet-50 rounded-lg"><ArrowsRightLeftIcon className="w-4 h-4 text-violet-600"/></button>
        )}
        <button onClick={() => openEdit(r)}       title="Edit"   className="p-1.5 hover:bg-indigo-50 rounded-lg"><PencilIcon className="w-4 h-4 text-indigo-600"/></button>
        <button onClick={() => handleDelete(r._id)} title="Delete" className="p-1.5 hover:bg-red-50  rounded-lg"><TrashIcon  className="w-4 h-4 text-red-500"  /></button>
      </div>
    )},
  ];

  const balance = viewing ? Number(viewing.total||0) - Number(viewing.paidAmount||0) : 0;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap items-center justify-between">
          <div className="flex gap-3 flex-1 flex-wrap">
            <div className="relative flex-1 min-w-44">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={'Search ' + title.toLowerCase() + '...'}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option value="">All Status</option>
              {statuses.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
          </div>
          <Button icon={PlusIcon} onClick={openCreate}>New {title.replace(/s$/, '')}</Button>
        </div>

        <Table columns={cols} data={records} loading={loading} emptyMessage={'No ' + title.toLowerCase() + ' found'} />
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>

      {/* Create / Edit */}
      <AccountingForm isOpen={formOpen} onClose={() => setFormOpen(false)} onSave={handleSave} editing={editing} defaultType={type} />

      {/* View detail */}
      <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} title={'Record: ' + (viewing?.referenceNo || '')} size="lg">
        {viewing && (
          <div className="space-y-4">
            <div className="flex justify-between items-start bg-indigo-50 rounded-2xl p-4">
              <div>
                <p className="text-xl font-bold font-mono text-gray-900">{viewing.referenceNo}</p>
                <p className="text-sm text-gray-500 mt-0.5">{format(new Date(viewing.date), 'dd MMMM yyyy')}</p>
                {viewing.convertedFrom && <p className="text-xs text-violet-500 mt-1">Converted from another document</p>}
                {viewing.convertedTo   && <p className="text-xs text-violet-500 mt-1">Already converted → further document exists</p>}
              </div>
              <div className="text-right space-y-1">
                <p className="text-2xl font-bold text-indigo-600">{'₹' + Number(viewing.total||0).toLocaleString('en-IN')}</p>
                <Badge status={viewing.status} />
                <div className="flex gap-3 mt-1 ml-auto justify-end">
                  <button onClick={() => downloadPdf(viewing)}
                    className="text-xs text-green-600 hover:underline flex items-center gap-1">
                    <ArrowDownTrayIcon className="w-3 h-3" /> Download PDF
                  </button>
                  <button onClick={() => { setViewOpen(false); openInvoice(viewing); }}
                    className="text-xs text-orange-500 hover:underline flex items-center gap-1">
                    <PrinterIcon className="w-3 h-3" /> Print
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Party</p>
                <p className="font-semibold text-gray-900">{viewing.contact?.name || viewing.contactName || '—'}</p>
                {viewing.contact?.company && <p className="text-gray-500 text-xs">{viewing.contact.company}</p>}
                {viewing.contact?.gstin   && <p className="font-mono text-xs text-gray-400">GSTIN: {viewing.contact.gstin}</p>}
              </div>
              <div className="space-y-1 text-xs">
                {viewing.dueDate      && <div className="flex justify-between"><span className="text-gray-400">Due Date</span><span className="font-medium">{format(new Date(viewing.dueDate), 'dd MMM yyyy')}</span></div>}
                {viewing.deliveryDate && <div className="flex justify-between"><span className="text-gray-400">Delivery</span><span className="font-medium">{format(new Date(viewing.deliveryDate), 'dd MMM yyyy')}</span></div>}
                {viewing.paymentMode  && <div className="flex justify-between"><span className="text-gray-400">Payment</span><span className="font-medium capitalize">{viewing.paymentMode.replace('_',' ')}</span></div>}
                {viewing.vehicleNo    && <div className="flex justify-between"><span className="text-gray-400">Vehicle</span><span className="font-medium">{viewing.vehicleNo}</span></div>}
              </div>
            </div>

            <div className="border border-gray-100 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>{['#','Description','Qty','Rate','Total'].map(h => (
                    <th key={h} className={'text-xs font-semibold text-gray-400 px-4 py-2.5 ' + (h==='#'||h==='Description'?'text-left':'text-right')}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(viewing.lineItems||[]).map((l,i) => (
                    <tr key={i}>
                      <td className="px-4 py-2.5 text-gray-400 text-xs">{i+1}</td>
                      <td className="px-4 py-2.5 text-gray-800">{l.description||'—'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{l.quantity}</td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{'₹'+Number(l.unitPrice||0).toFixed(2)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{'₹'+Number(l.total||0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 flex justify-end">
                <div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{'₹'+Number(viewing.subtotal||0).toFixed(2)}</span></div>
                  <div className="flex justify-between text-gray-500"><span>Tax</span><span>{'₹'+Number(viewing.taxTotal||0).toFixed(2)}</span></div>
                  {viewing.discount>0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>{'−₹'+Number(viewing.discount).toFixed(2)}</span></div>}
                  <div className="flex justify-between font-bold text-base text-gray-900 border-t border-gray-300 pt-1.5">
                    <span>Total</span><span className="text-indigo-600">{'₹'+Number(viewing.total||0).toFixed(2)}</span>
                  </div>
                  {viewing.paidAmount>0 && <>
                    <div className="flex justify-between text-green-600"><span>Paid</span><span>{'₹'+Number(viewing.paidAmount).toFixed(2)}</span></div>
                    <div className={'flex justify-between font-semibold '+(balance>0?'text-amber-600':'text-green-600')}>
                      <span>Balance</span><span>{'₹'+balance.toFixed(2)}</span>
                    </div>
                  </>}
                </div>
              </div>
            </div>
            {viewing.notes && (
              <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                <p className="text-xs font-semibold text-amber-600 mb-1">Notes</p>
                <p className="text-sm text-gray-700">{viewing.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Record Payment Modal ── */}
      <RecordPaymentModal
        isOpen={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        record={paymentRec}
        onSave={handleRecordPayment}
      />

      {/* ── Convert Modal ── */}
      <Modal isOpen={convertOpen} onClose={() => setConvertOpen(false)} title="Convert Document" size="sm">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Convert <span className="font-semibold font-mono">{convertRec?.referenceNo}</span> to:
          </p>
          <div className="space-y-2">
            {convertOptions.map(opt => (
              <button key={opt.value} onClick={() => handleConvert(opt.value)} disabled={converting}
                className="w-full text-left px-4 py-3 rounded-xl border-2 border-violet-200 hover:border-violet-500 hover:bg-violet-50 transition-colors text-sm font-semibold text-violet-700 disabled:opacity-50">
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400">The original document will be marked as converted and a new document will be created as a draft.</p>
        </div>
      </Modal>

      {/* Invoice print */}
      <InvoiceDownloadModal isOpen={invoiceOpen} onClose={() => setInvoiceOpen(false)} record={invoiceRec} />
    </>
  );
}

/* ── Record Payment Modal component ─────────────────────────── */
function RecordPaymentModal({ isOpen, onClose, record, onSave }) {
  const [form, setForm] = useState({ amount: '', paymentMode: 'cash', paymentDate: new Date().toISOString().split('T')[0] });
  const [saving, setSaving] = useState(false);

  const balance = record ? Number(record.total||0) - Number(record.paidAmount||0) : 0;

  const submit = async e => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (Number(form.amount) > balance)             { toast.error('Amount exceeds balance due'); return; }
    setSaving(true);
    try { await onSave(form); setForm({ amount:'', paymentMode:'cash', paymentDate: new Date().toISOString().split('T')[0] }); }
    finally { setSaving(false); }
  };

  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record Payment" size="sm">
      {record && (
        <form onSubmit={submit} className="space-y-4">
          <div className="bg-indigo-50 rounded-xl p-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Invoice Total</span><span className="font-bold">₹{Number(record.total||0).toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Already Paid</span><span className="text-green-600 font-semibold">₹{Number(record.paidAmount||0).toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between border-t border-indigo-200 pt-1"><span className="font-semibold text-gray-700">Balance Due</span><span className="font-bold text-amber-600">₹{balance.toLocaleString('en-IN')}</span></div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Amount Received (₹)</label>
              <input type="number" min="1" max={balance} step="0.01" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={`Max ₹${balance}`} className={inp} required />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Mode</label>
              <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))} className={inp}>
                {['cash','bank','cheque','upi','card','other'].map(m => (
                  <option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Payment Date</label>
              <input type="date" value={form.paymentDate} onChange={e => setForm(f => ({ ...f, paymentDate: e.target.value }))} className={inp} />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-indigo-600 rounded-xl text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
